import {
  Firestore,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import type { User } from "firebase/auth";

import { getUniqueUsername } from "./username.web";

export type DocSource = "signup" | "login" | "redirect-return";

// Idempotent first-sign-in Firestore doc creation for an OAuth/phone user.
// Reads users/<uid>; returns early if a doc already exists (`created: false`).
// Otherwise writes a fresh doc with the same shape signup.web.tsx already
// uses for the Google new-user path, and returns `created: true`.
//
// Both /login and /signup show terms text next to the OAuth buttons, so
// `termsAccepted: true` is justified regardless of which screen the user
// came from. `source` is recorded for analytics only and never gates
// behavior.
export const ensureUserDocExists = async (
  firestore: Firestore,
  user: User,
  options: { source: DocSource },
): Promise<{ created: boolean }> => {
  const userDocRef = doc(firestore, "users", user.uid);
  const existing = await getDoc(userDocRef);
  if (existing.exists()) {
    return { created: false };
  }

  const usernameBase =
    user.displayName || user.email?.split("@")[0] || "user";
  const generatedUsername = await getUniqueUsername(firestore, usernameBase);

  await setDoc(userDocRef, {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    createdAt: serverTimestamp(),
    providers: user.providerData.map((p) => p.providerId),
    termsAccepted: true,
    recipientMonthlyWAQuota: 1,
    username: generatedUsername,
    docSource: options.source,
    // Opt new users into Global-feed push notifications by default.
    // Existing users (whose docs pre-date this field) stay off — the
    // notification fan-out treats an absent field as false.
    notificationSettings: { globalFeedPosts: true },
  });

  return { created: true };
};
